import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import DiagnosticCta from "@/components/DiagnosticCta";
import Seo from "@/components/Seo";
import { CASE_STUDIES } from "@/data/revamp";
import { breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const SECTIONS = [
  { key: "challenge", label: "The challenge" },
  { key: "approach", label: "Our approach" },
  { key: "outcome", label: "The outcome" },
];

export default function CaseStudyPage() {
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!cs) return <Navigate to="/case-studies" replace />;

  return (
    <div className="bg-[var(--bg)]">
      <Seo
        title={`${cs.title} — Case Study`}
        description={`${cs.metric}. ${cs.challenge}`}
        path={`/case-studies/${cs.slug}`}
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Case Studies", path: "/case-studies" },
            { name: cs.title, path: `/case-studies/${cs.slug}` },
          ]),
        ]}
      />

      <section className="pt-40 pb-16 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--ink-50)] hover:text-[var(--ink)] transition-colors mb-10"
            data-testid="case-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All case studies
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1">
              {cs.segment}
            </span>
            {cs.services.map((s) => (
              <span
                key={s}
                className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] border border-[var(--line-strong)] px-2 py-1"
              >
                {s}
              </span>
            ))}
            {cs.draft && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] border border-dashed border-[var(--line-strong)] px-2 py-1">
                Draft — pending verification
              </span>
            )}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-display text-3xl sm:text-4xl md:text-6xl tracking-[-0.03em] leading-[1.0] text-[var(--ink)] max-w-4xl"
          >
            {cs.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="mt-6 font-serif-accent italic text-[var(--accent)] text-xl md:text-2xl"
          >
            {cs.metric}
          </motion.p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl space-y-14">
            {SECTIONS.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: 0.05 * i, ease: EASE }}
              >
                <p className="overline mb-4">{s.label}</p>
                <p className="text-[var(--ink-80)] leading-relaxed text-base md:text-lg">{cs[s.key]}</p>
              </motion.div>
            ))}
            <div className="border-l-2 border-[var(--accent)] pl-6">
              <p className="text-sm text-[var(--ink-70)] italic leading-relaxed">{cs.quote}</p>
              <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[var(--ink-50)]">
                Timeline · {cs.timeline}
              </p>
            </div>
          </div>
        </div>
      </section>

      <DiagnosticCta />
    </div>
  );
}
